/**
 * Pipeline Bottleneck Radar — what to actually DO about a role.
 *
 * Ported from Theresa's mockup (`actionsFor`), which turns the four lights into prioritised next
 * steps and lifts the non-green ones onto a top-level Action Board. The lights say a role is off;
 * these say which lever to pull, in the TA team's own vocabulary ("review interviewer calibration",
 * "review the case scoring bar"), which is the half a traffic light cannot carry.
 *
 * ⚠️ IT IS A RULE ENGINE, NOT A MODEL. Nothing here calls Anthropic: every sentence is derived from
 * figures the radar already holds. That is deliberate and worth keeping — the advice is then free,
 * instant, identical for two people looking at the same role, and testable without a network. If a
 * generated line is ever wanted, it should sit BESIDE these rather than replace them, because a
 * recommendation nobody can reproduce is one nobody can argue with.
 *
 * ── The one departure from the mockup: `unknown` produces NO action ──────────────────────────
 * The mockup was typed by hand, so every figure existed and every signal was green, yellow or red.
 * Live, a signal is often `unknown` — too thin a cohort, a posting Lever no longer publishes, a
 * role open four days. Advising "pipeline too thin, ramp up sourcing" off a cohort we cannot read
 * is exactly the false alarm `unknown` exists to prevent, and acting on it costs a recruiter a week.
 * So every rule below tests for a REAL verdict, never merely "not green".
 */
import type { RadarBenchmarks, RadarMeasures, RadarPosition } from '../types/taBottleneck';
import { interviewTargets } from './bottleneckRadar';
import { MIN_COHORT, PIPELINE_MULTIPLIER } from './bottleneckSignals';
import type { RoleEvaluation, SignalKey, SignalStatus } from './bottleneckSignals';

/** How many actions a card shows. Three is a week's work; a list of nine is a backlog. */
export const MAX_ACTIONS = 3;

/**
 * Above this many interviews a week, the target itself is the thing to question. At 20+/week a
 * role is not short of capacity, it is short of time — the mockup says so in the action, because
 * "add interview capacity" is not advice anyone can take at that number.
 */
export const IMPLAUSIBLE_WEEKLY_TARGET = 15;

export type ActionSeverity = 'red' | 'yellow' | 'green';

export interface RadarAction {
  /** Stable id for tests and for React keys — never the sentence, which is written to be edited. */
  id: string;
  severity: ActionSeverity;
  /** Lower sorts first WITHIN a severity. The mockup's own ordering, kept. */
  priority: number;
  text: string;
}

const SEVERITY_RANK: Record<ActionSeverity, number> = { red: 0, yellow: 1, green: 2 };

/** A verdict we can act on. `unknown` and `info` are not verdicts — see the header. */
const judged = (s: SignalStatus | undefined): s is 'green' | 'yellow' | 'red' =>
  s === 'green' || s === 'yellow' || s === 'red';

const statusOf = (evaluation: RoleEvaluation, key: SignalKey): SignalStatus | undefined =>
  evaluation.stages.flatMap((stage) => stage.signals).find((sig) => sig.key === key)?.status;

const round = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(v) ? '–' : String(Math.round(v * 10) / 10);

/**
 * When this role realistically lands its open hires, at the pace and conversions it has NOW.
 *
 * ⚠️ IT IS NOT A TIME-TO-FILL MEASUREMENT, and must never be presented as one. It is arithmetic on
 * the present: how many first interviews are needed to produce the remaining hires, divided by how
 * many are happening a week. A role doing two interviews a week against a target of twenty gets a
 * date a year out — which is the point, because "throughput is red" does not convey that and a
 * date does.
 *
 * Returns null rather than a guess whenever a term is missing or unjudgeable, and `blocked` with a
 * reason when the arithmetic has a real answer that is not a date (nothing is moving at all).
 */
export function projectFill(
  position: RadarPosition,
  m: RadarMeasures,
  bench: RadarBenchmarks
): { weeks: number; label: string } | { blocked: string } | null {
  const remaining = position.openHires;
  if (remaining <= 0) return null;

  const pace = m.s2InterviewsPerWeek;
  if (pace <= 0) return { blocked: 'nothing is predictable at 0 interviews a week — throughput has to start first' };

  /**
   * The role's OWN conversions, not the benchmark's: this answers "where does this role actually
   * end up", and a projection run on FINN's average rates would describe a role we do not have.
   * A thin cohort falls back to the benchmark instead of refusing, because a missing rate here
   * would silently drop a whole factor out of the product and make the date far too optimistic.
   */
  const rate = (own: { rate: number | null; n: number }, fallback: number | null): number | null => {
    if (own.rate != null && own.n >= MIN_COHORT) return own.rate / 100;
    return fallback != null && fallback > 0 ? fallback / 100 : null;
  };
  const c2 = rate(m.conv2, bench.conv2);
  const c3 = rate(m.conv3, bench.conv3);
  const accept = bench.accept != null && bench.accept > 0 ? bench.accept / 100 : null;
  if (c2 == null || c3 == null || accept == null) return null;

  const endToEnd = c2 * c3 * accept;
  if (endToEnd <= 0) return { blocked: 'a step is at 0% — a date is possible once candidates flow through it' };

  /**
   * A lag for the rounds themselves. The interviews needed do not all happen in week one, and the
   * last candidate still has to go through case, final and an offer. The mockup used roughly a
   * week and a bit per step; three steps is the funnel we measure.
   */
  const LAG_WEEKS = 4;
  const weeks = Math.round(remaining / endToEnd / pace + LAG_WEEKS);
  if (!Number.isFinite(weeks)) return null;
  if (weeks > 52) return { weeks, label: 'in over a year' };

  const when = new Date();
  when.setDate(when.getDate() + weeks * 7);
  return {
    weeks,
    label: when.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
  };
}

/**
 * The prioritised next steps for one role, worst first, capped at `MAX_ACTIONS`.
 *
 * Ordering is severity then the mockup's own priority numbers, so a red conversion problem (1)
 * outranks a red deadline projection (2) which outranks thin coverage (3) — the sequence a
 * recruiter would work in, not the order the signals happen to sit on the card.
 */
export function actionsFor(
  position: RadarPosition,
  evaluation: RoleEvaluation,
  bench: RadarBenchmarks
): RadarAction[] {
  const m = position.measures;
  if (!m) return [];

  const out: RadarAction[] = [];
  const push = (id: string, severity: ActionSeverity, priority: number, text: string) =>
    out.push({ id, severity, priority, text });
  const worse = (s: 'yellow' | 'red'): ActionSeverity => s;

  // ── Conversion (priority 1): the weakest judged step, named as a thing to review ────────────
  const convSteps: { key: SignalKey; measure: { rate: number | null }; advice: string }[] = [
    {
      key: 'conv1',
      measure: m.conv1,
      advice: 'applicants are dropping before the first interview — review the job ad and the screening bar',
    },
    {
      key: 'conv2',
      measure: m.conv2,
      advice: 'candidates are dropping in the early interview — review interviewer calibration and the interview guide',
    },
    {
      key: 'conv3',
      measure: m.conv3,
      advice: 'few cases turn into offers — review the case requirements and the scoring bar',
    },
  ];
  const benchOf: Record<string, number | null> = {
    conv1: bench.conv1,
    conv2: bench.conv2,
    conv3: bench.conv3,
  };
  const convJudged = convSteps
    .map((step) => ({ ...step, status: statusOf(evaluation, step.key) }))
    .filter((step) => judged(step.status));
  // First red, else first yellow — the funnel is a sequence, the same rule the bottleneck uses.
  const weakest =
    convJudged.find((step) => step.status === 'red') ||
    convJudged.find((step) => step.status === 'yellow');
  if (weakest) {
    push(
      `conversion:${weakest.key}`,
      worse(weakest.status as 'yellow' | 'red'),
      1,
      `${weakest.advice} (${round(weakest.measure.rate)}% against FINN's ${round(benchOf[weakest.key])}%).`
    );
  }

  // ── Throughput (4 and 5), and the deadline that follows from it (2) ────────────────────────
  const tp2 = statusOf(evaluation, 'throughput2');
  const tp3 = statusOf(evaluation, 'throughput3');
  /**
   * The SAME targets the lights were judged against — `interviewTargets` is called again rather
   * than the numbers being parsed back out of a sub-label. Two ways to get one figure eventually
   * disagree, and here the disagreement would be an action quoting a target the dot never used.
   */
  const targets = interviewTargets(position.openHires, bench);

  if (judged(tp2) && tp2 !== 'green') {
    /**
     * ⚠️ At an implausible target the advice INVERTS. "Add interview capacity" is not something
     * anyone can act on against twenty interviews a week — at that point the plan is the problem,
     * and the useful next step is a conversation about the deadline, not a push for more slots.
     */
    const implausible = (targets.s2 ?? 0) >= IMPLAUSIBLE_WEEKLY_TARGET;
    push(
      'throughput:s2',
      worse(tp2),
      4,
      implausible
        ? `Too few early interviews (${round(m.s2InterviewsPerWeek)}/wk against a target of ${round(targets.s2)}) — and that target is very high, so take the deadline and the runway back to the hiring manager rather than only adding slots.`
        : `Too few early interviews (${round(m.s2InterviewsPerWeek)}/wk against a target of ${round(targets.s2)}) — add interviewer capacity.`
    );
  }
  if (judged(tp3) && tp3 !== 'green') {
    push(
      'throughput:s3',
      worse(tp3),
      5,
      `Too few case interviews (${round(m.s3InterviewsPerWeek)}/wk against a target of ${round(targets.s3)}) — add case rounds.`
    );
  }

  if (tp2 === 'red' || tp3 === 'red') {
    const projection = projectFill(position, m, bench);
    if (projection && 'blocked' in projection) {
      push('deadline', 'red', 2, `The plan is at risk: ${projection.blocked}.`);
    } else if (projection) {
      push(
        'deadline',
        'red',
        2,
        `At this pace (${round(m.s2InterviewsPerWeek)} interviews a week) and these conversions, realistically filled ${projection.label} — about ${projection.weeks} weeks.`
      );
    }
  }

  // ── Pipeline coverage (3) ──────────────────────────────────────────────────────────────────
  const cov = statusOf(evaluation, 'coverage');
  if (judged(cov) && cov !== 'green') {
    const wanted = Math.max(1, position.openHires * PIPELINE_MULTIPLIER);
    const pct = Math.round((m.activeQualified / wanted) * 100);
    push(
      'coverage',
      worse(cov),
      3,
      cov === 'red'
        ? `The pipeline is thin (${pct}% of what this many hires needs) — step up sourcing, and look again at how narrow the criteria are.`
        : `Coverage is borderline (${pct}%) — sharpen sourcing before it runs dry.`
    );
  }

  // ── Offer acceptance (6) ───────────────────────────────────────────────────────────────────
  const acc = statusOf(evaluation, 'acceptance');
  if (judged(acc) && acc !== 'green') {
    push(
      'acceptance',
      worse(acc),
      6,
      acc === 'red'
        ? 'Offers are being turned down — review the package, how long the process takes, and the candidate experience.'
        : 'Offer acceptance is borderline — keep candidate engagement in view through the final rounds.'
    );
  }

  if (!out.length) return [{ id: 'on-track', severity: 'green', priority: 9, text: 'On track. Keep the pace.' }];

  return out
    .sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || a.priority - b.priority)
    .slice(0, MAX_ACTIONS);
}
